/**
 * ThemeSwitcher — top-bar swatch picker for the 10 global color themes plus a
 * light/dark/system toggle. Selecting a theme applies instantly (via
 * ColorThemeProvider, which sets data-color-theme + persists to localStorage +
 * settings-service). Mirrors the picker shown on the Settings/Appearance page.
 */

import { Palette, Sun, Moon, Monitor, Check } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { themeList } from '@/lib/themes'
import { useColorTheme } from '@/components/providers/ColorThemeProvider'
import { useTheme } from '@/components/providers/ThemeProvider'

export function ThemeSwitcher() {
  const { colorTheme, setColorTheme } = useColorTheme()
  const { theme, setTheme } = useTheme()

  const modes = [
    { value: 'light' as const, icon: Sun, label: 'Clair' },
    { value: 'dark' as const, icon: Moon, label: 'Sombre' },
    { value: 'system' as const, icon: Monitor, label: 'Auto' },
  ]

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full text-[hsl(var(--topbar-fg))] hover:bg-[hsl(var(--topbar-fg))]/15 hover:text-[hsl(var(--topbar-fg))]"
          aria-label="Thème de couleurs"
        >
          <Palette className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 bg-[hsl(var(--modal-bg))] text-[hsl(var(--modal-fg))] border-[hsl(var(--modal-border))]">
        {/* Light / Dark / System */}
        <div className="mb-3">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Mode
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {modes.map((m) => {
              const Icon = m.icon
              const active = theme === m.value
              return (
                <button
                  key={m.value}
                  onClick={() => setTheme(m.value)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-medium transition-all',
                    active
                      ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary/30'
                      : 'border-border hover:bg-accent',
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {m.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Color themes */}
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Thème de couleurs
        </p>
        <div className="grid grid-cols-2 gap-1.5">
          {themeList.map((th) => {
            const active = colorTheme === th.name
            return (
              <button
                key={th.name}
                onClick={() => setColorTheme(th.name)}
                title={th.description}
                className={cn(
                  'flex items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-all',
                  active
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                    : 'border-border hover:bg-accent',
                )}
              >
                <span
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-1 ring-black/10"
                  style={{ background: `linear-gradient(135deg, hsl(${th.topbar}) 0%, hsl(${th.swatch}) 100%)` }}
                >
                  {active && <Check className="h-3 w-3 text-white drop-shadow" />}
                </span>
                <span className="min-w-0 flex-1 truncate text-[12px] font-semibold">
                  {th.label}
                </span>
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
